import styled from '@emotion/styled';
import { CssBaseline, MuiThemeProvider } from '@material-ui/core';
import { History } from 'history';
import { ClassAttributes, Component, createElement } from 'react';
import { IntlProvider } from 'react-intl';
import { Router } from 'react-router';

import Config from '../core/Config';
import Dependency from '../core/Dependency';
import State from '../core/State';
import { jss, JssProvider } from '../customJss';
import GlobalStyles from '../GlobalStyles';
import theme from '../theme';
import { HEADBAR_HEIGHT } from './component/compound/HeadBar';
import SigningInIndicatorScreen from './component/primitive/SigningInIndicatorScreen';
import DocumentTitle from './connected/DocumentTitle';
import _FatalErrorScreen from './connected/FatalErrorScreen';
import _FormattedSnackbar from './connected/FormattedSnackbar';
import _HeadBar from './connected/HeadBar';
import NonFatalErrorSnackbars from './connected/NonFatalErrorSnackbars';
import _SignedInScreen from './connected/SignedInScreen';
import _SignedOutScreen from './connected/SignedOutScreen';
import { Provider as ConfigProvider } from './context/config';
import { Provider as DependencyProvider } from './context/dependency';
import { Provider as HistoryProvider } from './context/history';
import { Provider as StateProvider } from './context/state';

interface Props {
  config: Config;
  dependency: Dependency;
  history: History;
  state: State;
}

type RootComponentProps = {
  noHeadBar: boolean;
};

class Root extends Component<ClassAttributes<HTMLElement> & Props, Record<string, unknown>> {
  componentDidMount() {
    this.props.dependency.authenticationUsecase.signInWithPreviousSession();
  }

  componentDidCatch(err: Error) {
    this.props.dependency.errorReporter.reportError(err);
    this.props.dependency.errorPresenter.showAsFatal(err);
  }

  render() {
    return (
      <ConfigProvider value={this.props.config}>
        <DependencyProvider value={this.props.dependency}>
          <HistoryProvider value={this.props.history}>
            <StateProvider value={this.props.state}>
              <Router history={this.props.history}>
                <JssProvider jss={jss}>
                  <MuiThemeProvider theme={theme}>
                    <CssBaseline />
                    <GlobalStyles />
                    <IntlProvider locale={this.props.state.language} messages={this.props.state.translations}>
                      <RootElement
                        noHeadBar={!this.props.state.authentication.signedInUser || !!this.props.state.fatalError}
                      >
                        <DocumentTitle />

                        <StyledSigningInIndicatorScreen visible={this.props.state.authentication.fetching >= 1} />

                        <NonFatalErrorSnackbars />

                        <FormattedSnackbar />

                        <HeadBar />

                        <FatalErrorScreen />

                        <SignedOutScreen />

                        <SignedInScreen />
                      </RootElement>
                    </IntlProvider>
                  </MuiThemeProvider>
                </JssProvider>
              </Router>
            </StateProvider>
          </HistoryProvider>
        </DependencyProvider>
      </ConfigProvider>
    );
  }
}

const RootElement = styled('div')<RootComponentProps>(
  {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  ({ noHeadBar }) => ({
    paddingTop: noHeadBar ? 0 : HEADBAR_HEIGHT,
  }),
);

const HeadBar = styled(_HeadBar)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: HEADBAR_HEIGHT,
});

const FatalErrorScreen = styled(_FatalErrorScreen)({
  width: '100%',
  height: '100%',
});

const FormattedSnackbar = styled(_FormattedSnackbar)({
  zIndex: theme.zIndex.snackbar,
});

const SignedOutScreen = styled(_SignedOutScreen)({
  width: '100%',
  height: '100%',
});

const SignedInScreen = styled(_SignedInScreen)({
  width: '100%',
  height: '100%',
});

const StyledSigningInIndicatorScreen = styled(SigningInIndicatorScreen)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 10000,
});

export default Root;
